import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getEvolutionName } from './data/evolutions';
import './index.css';
import { 
  XMarkIcon, 
  SunIcon, 
  MoonIcon, 
  ListBulletIcon,
  Squares2X2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { SearchableSelect } from './components/SearchableSelect';

/* Estilos para las animaciones del slider */
const slideAnimationsStyle = document.createElement('style');
slideAnimationsStyle.textContent = `
@keyframes slideInFromRight {
  from { transform: translateX(40px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes slideInFromLeft {
  from { transform: translateX(-40px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}
`;
document.head.appendChild(slideAnimationsStyle);

interface ClassInfo {
  classEvo: number;
  className: string;
}

// Componente para renderizar un icono individual desde archivos SVG
const ItemIcon = ({ iconName, className = "" }: { iconName: string; className?: string }) => {
  return (
    <img 
      src={`/_assets/icons/${iconName}.svg`} 
      className={`h-6 w-6 ${className}`}
      alt=""
      style={{ filter: 'brightness(0) invert(1)' }} // Hacer los iconos blancos
    />
  );
};

// Función para obtener el icono correspondiente a cada categoría
const getCategoryIcon = (category: string) => {
  // Mapeo de categorías a nombres de archivos de iconos
  const iconMap: Record<string, string> = {
    'helmets': 'icon_93',       // Casco
    'armors': 'icon_94',      // Armadura
    'pants': 'icon_95',       // Pantalones
    'gloves': 'icon_157',     // Guantes
    'boots': 'icon_158',      // Botas
    // Iconos para el resto de categorías
    'swords': 'icon_161',
    'axes': 'icon_164',
    'maces': 'icon_162',      // Mazas
    'maces and scepters': 'icon_162', // Mazas y cetros
    'spears': 'icon_159',
    'shields': 'icon_90',
    'bows and crossbows': 'icon_163',
    'jewel and misc': 'icon_167',
    'pets and rings and misc': 'icon_170',
    'staffs': 'icon_160',
    'wings': 'icon_166',
    'wings and orbs and spheres': 'icon_166',
    'accessories': 'icon_167',
    'potions': 'icon_168',
    'scrolls': 'icon_171',
    'gems': 'icon_170'
  };

  // Obtener el nombre del icono o usar uno predeterminado
  const iconName = iconMap[category.toLowerCase()] || 'icon_69';
  
  return <ItemIcon iconName={iconName} />
};

interface GameItem {
  Item: string;
  ItemCatID: number;
  ItemCat: string;
  MapID: number;
  MapDrop: string;
  MonsterIndex: number;
  Monster: string;
  isActive: boolean;
  allowbyClass: ClassInfo[];
  ModelFile: string;
  ItemIndex: number;
  // Nuevos campos
  Exc?: boolean;
  SocketCount?: number;
  Rate?: string;
}

// Lista fija de clases base estándar de MU Online
// Orden de clases segun las abreviaturas en server.php: DK, DW, FF, MG, DL, SM, RF, GL, RW, SLA, GC, LW, LM, IK, AC
const baseClassesList = [
  'Dark Knight',     // DK
  'Dark Wizard',     // DW
  'Fairy Elf',       // FF
  'Magic Gladiator', // MG
  'Dark Lord',       // DL
  'Summoner',        // SM
  'Rage Fighter',    // RF
  'Grow Lancer',     // GL
  'Rune Wizard',     // RW
  'Slayer',          // SLA
  'Gun Crusher',     // GC
  'White Wizard',    // LW
  'Lemuria Mage',    // LM
  'Illusion Knight', // IK
  'Alchemist',       // AC
];

function App() {
  const [items, setItems] = useState<GameItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<GameItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedMap, setSelectedMap] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [darkMode, setDarkMode] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const itemsPerPage = 24;
  const [sliderIndex, setSliderIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [animationDirection, setAnimationDirection] = useState<'left' | 'right' | null>(null);
  const [previousIndex, setPreviousIndex] = useState(0);
  const visibleCount = 7;
  const maxIndex = Math.max(0, baseClassesList.length - visibleCount);
  const visibleClasses = baseClassesList.slice(sliderIndex, sliderIndex + visibleCount);

  // Función para cambiar el índice del slider con animación
  const changeSliderIndex = useCallback((newIndex: number) => {
    // Guardamos el índice actual para saber la dirección
    setPreviousIndex(sliderIndex);
    // Determinamos la dirección de la animación
    setAnimationDirection(newIndex > sliderIndex ? 'left' : 'right');
    // Actualizamos el índice
    setSliderIndex(newIndex);
    
    // Limpiamos la dirección de animación después de un breve tiempo
    setTimeout(() => {
      setAnimationDirection(null);
    }, 2000); // Tiempo suficiente para que termine la animación
  }, [sliderIndex]);

  // Efecto para el autoplay del slider de clases
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (autoplay) {
      interval = setInterval(() => {
        const newIndex = sliderIndex >= maxIndex ? 0 : sliderIndex + 1;
        changeSliderIndex(newIndex);
      }, 5000); // Cambiar cada 3 segundos
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoplay, maxIndex, sliderIndex, changeSliderIndex]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setLoadingProgress(10);
        setLoadingStatus('Starting connection with the database...');
        
        // URL del script PHP que se conecta a tu servidor MySQL
        const apiUrl = 'https://drops.elgasiamu.com/api/server.php';
        
        setLoadingProgress(20);
        setLoadingStatus('Connecting to server...');
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`Error HTTP: ${response.status}`);
        }
        
        setLoadingProgress(50);
        setLoadingStatus('Downloading items data...');
        
        const data = await response.json();
        
        setLoadingProgress(80);
        setLoadingStatus('Processing items...');
        
        if (Array.isArray(data) && data.length > 0) {
          setLoadingStatus(`Loading ${data.length} items...`);
          setLoadingProgress(90);
          
          setItems(data);
          setFilteredItems(data);
          setError(null); // Aseguramos que no hay error si tenemos datos
          setLoadingProgress(100);
          setLoadingStatus('Loading complete');
        } else {
          // Si la respuesta es válida pero no hay datos
          setError('No data found in the server.');
          setItems([]);
          setFilteredItems([]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos desde el servidor:', error);
        setError('No data could be loaded. Please check your connection or try again later.');
        
        setLoadingProgress(100); // Completamos la barra para indicar que terminó
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => {
    let result = items;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(item =>
        item.Item.toLowerCase().includes(searchLower) ||
        item.Monster.toLowerCase().includes(searchLower)
      );
    }

    if (selectedCategory) {
      result = result.filter(item => item.ItemCat === selectedCategory);
    }

    if (selectedMap) {
      result = result.filter(item => item.MapDrop === selectedMap);
    }

    if (selectedClass) {
      result = result.filter(item => 
        item.allowbyClass.some(cls => cls.className === selectedClass)
      );
    }

    setFilteredItems(result);
    setPage(1);
  }, [searchTerm, selectedCategory, selectedMap, selectedClass, items]);

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedMap('');
    setSelectedClass('');
  };

  const categories = Array.from(new Set(items.map(item => item.ItemCat))).sort();
  const maps = Array.from(new Set(items.map(item => item.MapDrop))).sort();
  // En lugar de extraer y ordenar las clases de los items, usamos el orden predefinido en baseClassesList
  // Esto garantiza que el orden visual coincida con el orden deseado (DK primero, AC último)
  const classes = baseClassesList;

  // La siguiente línea se comentó porque no se estaba utilizando
  // const baseClasses = Array.from(new Set(classes.map(cls => cls.split(' (')[0])));

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handlePageChange = (value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Efecto para aplicar/quitar la clase dark al elemento HTML según el estado darkMode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      document.body.classList.add('light');
    }
  }, [darkMode]);

  // No renderizamos un loading para toda la pantalla, se mostrará solo en la sección de ítems

  return (
    <div className={`app min-h-screen ${darkMode ? 'dark bg-gray-900' : 'light bg-gray-100'}`}>
      {/* Header con efecto blur */}
      <header className="top-0 z-50 w-full backdrop-blur-md bg-gray-800/70 dark:bg-gray-950/60 border-b border-gray-700/30 shadow-lg">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            {/* Logo a la izquierda */}
            <div className="flex items-center">
              <img 
                src="/img/elgasiatop.png" 
                alt="Logo" 
                className="h-16 mr-3 filter grayscale" 
                style={{ filter: 'grayscale(0)' }}
                onError={(e) => {
                  // Fallback si la imagen no existe
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = 'https://via.placeholder.com/180x40?text=YourLogo';
                }}
              />
            </div>
            
            {/* Conteo de items a la derecha */}
            <div className="flex items-center space-x-4">
              {/* Enlaces del menú */}
              {/*
              <div className="hidden md:flex items-center space-x-3 mr-2">
                <a 
                  href="/events" 
                  className="px-3 py-1.5 rounded-md bg-gray-700/50 hover:bg-gray-600/80 text-white font-medium text-sm transition-colors"
                >
                  Events
                </a>
                <a 
                  href="/drops-xml" 
                  className="px-3 py-1.5 rounded-md bg-gray-700/50 hover:bg-gray-600/80 text-white font-medium text-sm transition-colors"
                >
                  Drops XML
                </a>
              </div>
              */}
              <div className="hidden md:flex items-center">
                <span className="px-3 py-1.5 rounded-full bg-gray-700/50 text-white font-medium text-sm">
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading...
                    </span>
                  ) : (
                    <span>Total Items: <span className="font-bold text-primary-400">{items.length}</span></span>
                  )}
                </span>
              </div>
              
              {/* Dark Mode Toggle }
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full bg-gray-700/40 hover:bg-gray-600/60 text-gray-300 transition-colors duration-200"
                title="Toggle Dark Mode"
              >
                {darkMode ? (
                  <SunIcon className="h-5 w-5 text-yellow-300" />
                ) : (
                  <MoonIcon className="h-5 w-5 text-blue-400" />
                )}
              </button>
              { Dark Mode Toggle */}
              
              {/* View Mode Toggle */}
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="p-2 rounded-full bg-gray-700/40 hover:bg-gray-600/60 text-gray-300 transition-colors duration-200"
                title="Toggle View Mode"
              >
                {viewMode === 'grid' ? (
                  <ListBulletIcon className="h-5 w-5" />
                ) : (
                  <Squares2X2Icon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Espacio para el contenido principal, sin botones duplicados */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {/* Espacio para contenido adicional si es necesario */}
          </h1>
          <div className="flex items-center gap-4">
            {/* Los botones de alternancia se movieron al header */}
          </div>
        </div>

        <div className="flex flex-col items-center mb-8">
          {/*h1 className="text-5xl font-bold text-white drop-shadow-lg mb-8 mt-8">
            Drop List
          </h1*/}
          <div className="relative w-full flex flex-col items-center justify-center">
            <div
              className="flex flex-row gap-8 mb-2 px-2 justify-center items-center w-full"
              style={{ maxWidth: '100vw' }}
            >
              {visibleClasses.map((baseCls) => (
                <button
                  key={baseCls}
                  onClick={() => {
                    // Buscar todas las clases relacionadas (base + evoluciones)
                    const related = classes.filter(cls => cls.startsWith(baseCls));
                    setSelectedClass(baseCls);
                    setFilteredItems(
                      items.filter(item =>
                        item.allowbyClass.some(cls => related.includes(cls.className))
                      )
                    );
                    setPage(1);
                  }}
                  className={`group flex flex-col items-center transition-all duration-300 ${selectedClass && selectedClass.startsWith(baseCls) ? 'scale-110 animate-pulse' : 'opacity-80 hover:scale-105'}`}
                  style={{
                    minWidth: 100,
                    transition: 'all 0.3s ease',
                    animation: (() => {
                      const idx = baseClassesList.indexOf(baseCls);
                      // Solo animar los elementos que se acaban de agregar al slider
                      if (animationDirection === 'left' && idx === sliderIndex + visibleCount - 1) {
                        return 'slideInFromRight 0.3s ease forwards';
                      } else if (animationDirection === 'right' && idx === sliderIndex) {
                        return 'slideInFromLeft 0.3s ease forwards';
                      }
                      return '';
                    })()
                  }}
                >
                  <div className="relative flex flex-col items-center" style={{ width: 80 }}>
                    <div className="relative flex items-center justify-center" style={{ width: 80, height: 80 }}>
                      {/* Imagen del personaje, centrada y contenida */}
                      <img
                        src={`/class/${baseCls.replace(/\s+/g, '')}Image.webp`}
                        alt={baseCls}
                        className={`w-14 h-14 object-contain z-10 rounded-full transition-all duration-200 ${selectedClass && selectedClass.startsWith(baseCls) ? 'ring-2 ring-orange-400 shadow-lg shadow-orange-400/80' : ''}`}
                        style={{ background: 'rgba(0,0,0,0.0)' }}
                      />
                      {/* Aro decorativo, centrado */}
                      <img
                        src="/hero-slider-mini-bg.png"
                        alt="Aro"
                        className="absolute left-0 top-0 w-20 h-20 z-20 pointer-events-none select-none"
                        draggable="false"
                      />
                    </div>
                    <span className={`text-xs font-semibold text-white text-center px-2 py-1 rounded bg-gray-900/80 shadow-md mt-0 truncate w-24 ${selectedClass && selectedClass.startsWith(baseCls) ? 'font-bold' : 'opacity-80 group-hover:opacity-100'}`}>{baseCls}</span>
                  </div>
                </button>
              ))}
            </div>
            {/* Flechas debajo del slider */}
            <div className="flex flex-row gap-8 items-center justify-center mt-6 mb-4">
              <button
                className="flex items-center justify-center h-10 w-10 rounded-full bg-gray-800/80 hover:bg-primary-500/80 transition-colors duration-200 focus:outline-none disabled:opacity-30"
                onClick={() => changeSliderIndex(Math.max(0, sliderIndex - 1))}
                disabled={sliderIndex === 0}
                aria-label="Previous"
              >
                <ChevronLeftIcon className="w-6 h-6 text-gray-300 group-hover:text-white" />
              </button>
              
              {/* Botón para pausar/reanudar autoplay */}
              <button
                className="flex items-center justify-center h-10 w-10 rounded-full bg-gray-800/80 hover:bg-primary-500/80 transition-colors duration-200 focus:outline-none"
                onClick={() => setAutoplay(!autoplay)}
                aria-label={autoplay ? "Pause" : "Play"}
              >
                {autoplay ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 text-gray-300">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 text-gray-300">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                )}
              </button>
              
              <button
                className="flex items-center justify-center h-10 w-10 rounded-full bg-gray-800/80 hover:bg-primary-500/80 transition-colors duration-200 focus:outline-none disabled:opacity-30"
                onClick={() => changeSliderIndex(Math.min(maxIndex, sliderIndex + 1))}
                disabled={sliderIndex === maxIndex}
                aria-label="Next"
              >
                <ChevronRightIcon className="w-6 h-6 text-gray-300 group-hover:text-white" />
              </button>
            </div>
          </div>
        </div>

        <div className="sticky top-0 z-[60] rounded-lg bg-gray-900 text-white dark:bg-gray-950 px-4 py-4 my-4 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="text"
              placeholder="Items or Monsters"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input flex-1"
            />

            <SearchableSelect
              options={categories}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="Select Category"
              label="Category"
            />

            <SearchableSelect
              options={maps}
              value={selectedMap}
              onChange={setSelectedMap}
              placeholder="Select Map"
              label="Map"
            />

            <SearchableSelect
              options={classes}
              value={selectedClass}
              onChange={setSelectedClass}
              placeholder="Select Class"
              label="Class"
            />

            <button
              onClick={resetFilters}
              className="btn btn-secondary flex items-center gap-2"
            >
              <XMarkIcon className="h-5 w-5" />
              Reset
            </button>
          </div>

          {/* Barra de iconos de categorías */}
          <div className="w-full bg-gray-900/90 border border-gray-800 rounded-lg py-2.5 px-3 mb-3">
            <div className="overflow-x-auto no-scrollbar">
              <div className="grid grid-flow-col auto-cols-max gap-3 px-2 py-2 justify-start items-center">
                {/* Elemento no categorizado */}
                <div 
                  onClick={() => setSelectedCategory('')}
                  title="All Items"
                  className={`flex items-center justify-center cursor-pointer transition-all duration-150 hover:scale-105 p-1.5 rounded-full ${selectedCategory === '' ? 'bg-primary-600/50 dark:bg-primary-800/70 ring-1 ring-primary-500' : 'hover:bg-gray-800'}`}
                >
                  <div className="h-9 w-9 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-950 rounded-full border border-gray-600 shadow-inner shadow-black">
                    <Squares2X2Icon className="h-6 w-6 text-white drop-shadow-sm" />
                  </div>
                </div>
                
                {/* Mostrar iconos para cada categoría con efecto hover y tooltip */}
                {categories.map((category) => (
                  <div 
                    key={category}
                    onClick={() => setSelectedCategory(selectedCategory === category ? '' : category)}
                    title={category} /* Tooltip que muestra el nombre de la categoría */
                    className={`flex items-center justify-center cursor-pointer transition-all duration-150 hover:scale-105 p-1.5 rounded-full ${selectedCategory === category ? 'bg-primary-600/50 dark:bg-primary-800/70 ring-1 ring-primary-500' : 'hover:bg-gray-800'}`}
                  >
                    <div className="h-9 w-9 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-950 rounded-full border border-gray-600 shadow-inner shadow-black">
                      {getCategoryIcon(category)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Texto informativo */}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredItems.length} items
          </p>
        </div>

        <div className="rounded-lg bg-gray-900 dark:bg-gray-950 px-4 py-4 min-h-[300px]" style={{marginBottom: '100px'}}>
          {loading ? (
            <div className="flex flex-col justify-center items-center h-[300px] w-full">
              <div className="w-64 bg-gray-700 rounded-full h-2.5 mb-6">
                <div 
                  className="bg-primary-600 h-2.5 rounded-full transition-all duration-300" 
                  style={{ width: `${loadingProgress}%` }}
                ></div>
              </div>
              <div className="text-gray-300 dark:text-gray-400 text-sm mb-8">
                {loadingStatus}
              </div>
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
            </div>
          ) : error && items.length === 0 ? (
            <div className="flex flex-col justify-center items-center h-[300px] text-center">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mb-4" />
              <div className="text-red-500 dark:text-red-400 text-lg font-semibold mb-2">Error de conexión</div>
              <p className="text-gray-400 dark:text-gray-500 text-sm">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 btn btn-primary flex items-center gap-2"
              >
                <ArrowPathIcon className="h-5 w-5" />
                Reintentar
              </button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col justify-center items-center h-[300px] text-center">
              <div className="text-gray-500 dark:text-gray-400 text-lg mb-2">No items found</div>
              <p className="text-gray-400 dark:text-gray-500 text-sm">Try with other search criteria</p>
              <button
                onClick={resetFilters}
                className="mt-4 btn btn-primary flex items-center gap-2"
              >
                <XMarkIcon className="h-5 w-5" />
                Reset Filters
              </button>
            </div>
          ) : (
            <div className={viewMode === 'grid' 
              ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2"
              : "space-y-3"
            }>
              {/* Eliminar el caracter '0' de la renderización */}
              <style>{`
                /* Solución simple: cubrir solo el "0" con un parche negro */
                .item-container::before {
                  content: "" !important;
                  display: block !important;
                  position: absolute !important;
                  top: 0 !important;
                  left: 0 !important;
                  width: 18px !important;
                  height: 20px !important;
                  background-color: #080b0f !important;
                  z-index: 20 !important;
                  color: #080b0f !important;
                  pointer-events: none !important;
                  border-bottom-right-radius: 5px !important;
                }
              
              `}</style>
              {currentItems.map((item, index) => (
                <div
                  key={`item-${index}`}
                  className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden ${
                    viewMode === 'list' ? 'flex' : ''
                  }`}
                >
                  <div className={`${viewMode === 'list' ? 'flex gap-4 w-full' : ''} p-2`}>
                    <div className={`${viewMode === 'list' ? 'flex gap-4 w-full' : 'flex flex-col gap-2'}`}>
                      {/* Nuevo contenedor de imagen que evita el 0 usando div con ::after para reemplazar el contenido */}
                      <div 
                        className={`item-container ${viewMode === 'list' ? 'w-24' : 'w-full'} aspect-square bg-black/80 rounded-lg border-2 ${item.Exc ? 'border-green-500' : item.SocketCount && item.SocketCount > 0 ? 'border-purple-500' : 'border-primary-500'} shadow-lg p-1.5 relative overflow-hidden`}
                        data-item-id={`${item.ItemCatID}-${item.ItemIndex}`}
                        data-item-name={item.Item.replace(/\d+$/, '')}
                        // Sobreescribir cualquier texto que pueda estar causando el 0
                        data-item-value=""
                      >
                        {/* Fondo oscuro para combinar con el tema dark */}
                        <div className="absolute inset-0 bg-gray-900/80 opacity-20"></div>
                        
                        {/* Contenedor exclusivo para la imagen con z-index alto */}
                        <div className="absolute inset-0 flex items-center justify-center z-20">
                          <img
                            src={`/items/${item.ItemCatID}-${item.ItemIndex}.gif`}
                            alt={item.Item.replace(/\d+$/, '')}
                            loading="lazy"
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder.png';
                            }}
                          />
                        </div>

                        {/* Badges para ítems especiales, posicionados encima de todo */}
                        <div className="absolute inset-0 pointer-events-none z-30">
                          {/* Badge para Excellent */}
                          {item.Exc && (
                            <div className="absolute top-0 right-0 bg-green-500 text-black text-xs font-bold px-1.5 py-0.5 rounded-bl shadow-md pointer-events-auto">
                              Excellent Item
                            </div>
                          )}
                          
                          {/* Badge para Socket */}
                          {item.SocketCount && item.SocketCount > 0 && (
                            <div className={`absolute ${item.Exc ? 'top-7' : 'top-0'} right-0 bg-purple-500 text-white text-xs font-bold px-1.5 py-0.5 ${item.Exc ? 'rounded-br' : 'rounded-bl'} shadow-md pointer-events-auto`}>
                              Socket Item
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex flex-col gap-1.5">
                          <div>
                            <h2 className={`text-base font-semibold line-clamp-1 ${item.Exc ? 'text-green-500 dark:text-green-400' : (item.SocketCount && item.SocketCount > 0) ? 'text-purple-500 dark:text-purple-400' : 'text-gray-900 dark:text-white'}`}>
                              {item.Item.replace(/\d+$/, '')}
                            </h2>
                            <div className="mt-1 flex flex-wrap gap-1">
                              <span className="px-1.5 py-0.5 text-xs rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-100">
                                {item.ItemCat}
                              </span>
                              <span className="px-1.5 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100">
                                {item.MapDrop}
                              </span>
                              <span className="px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                                {item.Monster}
                              </span>
                            </div>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                            Model: 
                            <span className="mx-1 px-2 py-2 text-xs rounded-full bg-primary-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100">
                              {item.ModelFile}
                            </span>
                          </span>
                        </div>

                        <div className="mt-1.5">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                            Classes allowed:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {item.allowbyClass && item.allowbyClass.length > 0 ? (
                              // Si hay clases específicas permitidas, mostrarlas
                              item.allowbyClass.map((cls, idx) => (
                                <span
                                  key={idx}
                                  className="px-1 py-0.5 text-xs rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 flex items-center gap-1"
                                >
                                  <span className="w-1 h-1 rounded-full bg-green-500"></span>
                                  {getEvolutionName(cls.className, cls.classEvo)}
                                </span>
                              ))
                            ) : (
                              // Si no hay restricciones (array vacío), mostrar que es para todas las clases
                              <span className="px-1.5 py-0.5 text-xs rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-100">
                                All Classes
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Efecto de blur progresivo usando las clases CSS */}
        {totalPages > 1 && (
          <div className="gradient-blur">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        )}
        
        {/* Paginación */}
        {totalPages > 1 && (
          <div style={{ position: 'fixed', bottom: '0', left: '0', right: '0', zIndex: 50, display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
            <nav className="flex items-center gap-2 bg-gray-800/70 p-4 rounded-xl shadow-lg border border-gray-700/50">
              {/* Botón Anterior */}
              <button
                onClick={() => page > 1 && handlePageChange(page - 1)}
                disabled={page === 1}
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 ${page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-600 hover:text-white'} bg-gray-700 text-gray-300`}
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              
              {/* Números de página */}
              <div className="flex items-center">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                  // Mostrar solo página actual, primera, última y 1 o 2 páginas adyacentes
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= page - 1 && pageNum <= page + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 mx-1 font-medium rounded-full transition-all duration-200 ${
                          page === pageNum
                            ? 'bg-primary-600 text-white shadow-md shadow-primary-500/30 ring-2 ring-primary-400/50 transform scale-110'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600 dark:hover:bg-gray-600 hover:text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    (pageNum === 2 && page > 3) ||
                    (pageNum === totalPages - 1 && page < totalPages - 2)
                  ) {
                    // Mostrar puntos suspensivos
                    return (
                      <span key={pageNum} className="mx-1 text-gray-400 dark:text-gray-500">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
              
              {/* Botón Siguiente */}
              <button
                onClick={() => page < totalPages && handlePageChange(page + 1)}
                disabled={page === totalPages}
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 ${page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-600 hover:text-white'} bg-gray-700 text-gray-300`}
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
